const DEFAULT_READ_TIMEOUT_MS = 10_000;
const DEFAULT_WRITE_TIMEOUT_MS = 20_000;

export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

/**
 * The locale the player is reading the app in, as an Accept-Language value.
 *
 * Read from storage rather than the i18n store to keep this module free of a
 * store import (httpClient is imported by the store's own consumers). The key
 * is the one i18nStore persists.
 */
function acceptLanguage(): string {
  try {
    const stored = window.localStorage.getItem('sorteia:lang');
    if (stored === 'en' || stored === 'es') return stored;
    if (stored === 'pt-BR') return 'pt-BR,pt;q=0.9';
  } catch {
    // Private mode: fall through to the default below.
  }
  return 'pt-BR,pt;q=0.9';
}

function activeCountryCode(): string {
  try {
    const stored = String(window.localStorage.getItem('sorteia:country') || '').trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(stored)) return stored;
  } catch {
    // Private mode: use the original single-country default.
  }
  return 'BR';
}

/**
 * The Content-Type header apiFetch should default to for a given body, absent
 * an explicit override in `options.headers`. A `FormData` body must NOT get a
 * forced `application/json` here: per the fetch spec, once the caller sets
 * Content-Type explicitly the browser will not substitute its own
 * `multipart/form-data; boundary=...` value, so a multipart upload would ship
 * with a JSON content type the server cannot parse.
 */
export function defaultContentType(body: RequestInit['body']): Record<string, string> {
  if (!body || body instanceof FormData) return {};
  return { 'Content-Type': 'application/json' };
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const method = String(options.method || 'GET').toUpperCase();
  const timeoutMs = method === 'GET' ? DEFAULT_READ_TIMEOUT_MS : DEFAULT_WRITE_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(path, {
      ...options,
      signal: options.signal || controller.signal,
      headers: {
        ...defaultContentType(options.body),
        // Server-rendered copy (push notifications above all) needs to know
        // which locale to answer in; without this every player got English.
        'Accept-Language': acceptLanguage(),
        // Public room discovery uses the selected registration country. Once
        // authenticated, protected endpoints derive the authoritative country
        // from users.country_code and do not trust this header.
        'X-Sorteia-Country': activeCountryCode(),
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const code = payload && typeof payload === 'object' && 'code' in payload ? String(payload.code) : '';
      throw new ApiError(code || `HTTP_${response.status}`, response.status, payload);
    }
    return payload as T;
  } finally {
    window.clearTimeout(timer);
  }
}

export function authHeader(token: string | null | undefined): HeadersInit {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value);
}
